import axios, { AxiosError, isCancel } from './axios';
import { GetPageRequestID } from './customer-api/pagerequests';
import { Visible } from './dom';
import { ReportValidity } from './form';
import * as object from './object';
import { MaybePromise } from './promise';
import * as regexp from './regexp';
import * as strings from './strings';
import { CapitalizeFirstLetter } from './util';

export function IsAxiosError<T>(err: unknown): err is AxiosError<T> {
    if (object.IsObject(err) && object.HasKey(err, 'isAxiosError')) {
        return true;
    }

    return false;
}

export function GetAxiosParamErrors(err: unknown): ParamErrors | undefined {
    if (object.IsObject(err) && object.HasKey(err, 'response') && object.IsObject(err['response']) && object.HasKey(err['response'], 'status') && IsParamErrors(err['response']['data'])) {
        return err['response']['data'];
    }
}

export declare interface ParamErrors {
    errors: {
        param?: string;
        code?: string;
        message: string;
    }[];
}

export function IsParamErrors(data: unknown): data is ParamErrors {
    if (!(object.IsObject(data) && object.HasKey(data, 'errors') && data['errors'] instanceof Array)) {
        return false;
    }

    // we don't know that it's *not* errors if there's nothing in the list
    if (!data['errors'].length) {
        return true;
    }

    for (const err of data['errors']) {
        if (!(object.IsObject(err) && object.HasKey(err, 'message'))) {
            return false;
        }
    }

    return true;
}

declare interface bindErrors {
    errorKind: 'input' | 'type';
    [param: string]: string;
}

export function IsBindErrors(data: unknown): data is bindErrors {
    return object.IsObject(data) &&
        object.HasKey(data, 'errorKind') &&
        (data['errorKind'] === 'input' || data['errorKind'] === 'type');
}

export type ShowErrorFunc = (msg: string, err: unknown) => void;

export const Defaults = {
    ShowErrorFunc: alert as ShowErrorFunc | undefined,
};

export function HandleRequestError(
    err: unknown,
    parent: HTMLElement = document.body,
    showError = Defaults.ShowErrorFunc,
    paramMappings?: Record<string, string>) {
    if (object.IsObject(err) && err['message'] === 'Request aborted') {
        return;
    }
    if (isCancel(err)) {
        return;
    }

    const isBindErrors = Boolean(IsBindErrors(err) || (IsAxiosError(err) && err.response && IsBindErrors(err.response?.data) && err.response?.data.errorKind === 'input'));
    const isParamErrors = Boolean(IsParamErrors(err) || (IsAxiosError(err) && err.response && IsParamErrors(err.response?.data) && err.response?.data.errors.length));

    if (isBindErrors || isParamErrors) {
        const fields = new Map<string, string>();
        const messages: string[] = [];

        if (isBindErrors) {
            if (!IsBindErrors(err)) {
                err = (err as AxiosError).response?.data as bindErrors;
            }

            for (const k of Object.keys(err as bindErrors)) {
                if (k === 'errorKind') {
                    continue;
                }
                fields.set(k, (err as bindErrors)[k]);
            }
        } else {
            if (!IsParamErrors(err)) {
                err = (err as AxiosError).response?.data as ParamErrors;
            }

            for (const p of (err as ParamErrors)['errors']) {
                if (!p.param) {
                    messages.push(p.message);
                    continue;
                }

                fields.set(p.param, p.message);
            }
        }

        let invalidControl: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | undefined;

        const errElements = parent.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-api],[name]:not([data-api])');
        for (const [k] of fields) {
            const matches = Array.from(errElements).filter(e => new RegExp(`(^|,)${regexp.Escape(paramMappings?.[k] || k)}(,|$)`, 'i').test(e.getAttribute('data-api') || e.getAttribute('name') || ''));
            for (const e of matches) {
                if (!e.setCustomValidity) {
                    continue;
                }
                if (!Visible(e)) {
                    continue;
                }

                // only check for things that HTML5 attributes don't already cover
                // e.g. min & required are already attributes
                let message: string = strings.Capitalize(fields.get(k) ?? '');
                if (fields.get(k) === 'tel') {
                    message = 'Please enter a valid phone number.';
                }
                e.setCustomValidity(message);
                e.addEventListener('input', () => e.setCustomValidity(''));
                e.addEventListener('change', () => e.setCustomValidity(''));

                if (!invalidControl && (e instanceof HTMLInputElement || e instanceof HTMLSelectElement || e instanceof HTMLTextAreaElement)) {
                    invalidControl = e;
                }

                fields.delete(k);
                break;
            }
        }

        setTimeout(() => {
            if (invalidControl) {
                ReportValidity(invalidControl);
            }
        });

        // do we still have errors that we couldn't find a field for?
        if (fields.size) {
            for (const [param, message] of fields) {
                let p = param;
                if (err instanceof SMGError) {
                    p = `${err.Scope ? `${err.Scope} ` : ''}${p}`;
                }

                if (!message) {
                    messages.push(`${p} is invalid`);
                } else {
                    messages.push(`${p}: ${message}`);
                }
            }
        }

        if (messages.length && showError) {
            showError(`${CapitalizeFirstLetter(messages.join(', '))}.`, err);
        }
    } else if (typeof err !== 'undefined') {
        if (showError) {
            let message: string = '';

            if (IsAxiosError(err) && object.IsObject(err.response?.data)) {
                if (typeof err.response?.data['message'] === 'string') {
                    message = err.response?.data['message'];
                } else if (typeof err.response?.data['error'] === 'string') {
                    message = err.response?.data['error'];
                }
            }

            if (!message && err instanceof Error) {
                message = err.message;
            }

            showError(message || String(err), err);
            console.error(err);
        } else {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw err;
        }
    }
}

export interface SMGErrorOptions {
    scope?: string;
}

export class SMGError extends Error {
    Err: Error;
    Scope: SMGErrorOptions['scope'];

    constructor(err: Error | string, opts: SMGErrorOptions = {}) {
        if (typeof err === 'string') {
            err = new Error(err);
        }

        super(err.message);
        this.Err = err;

        Object.assign(this, err);

        this.Scope = opts.scope;
    }

    'toString'(): string {
        return `${this.Scope ? `${this.Scope}: ` : ''}${this.message}`;
    }

    Unwrap(): Error {
        return this.Err;
    }
}

function isIgnoredError(msg: string): boolean {
    switch (msg) {
    case 'Request aborted':
    case 'Network Error':
    case 'Script error.':
    case 'timeout exceeded':
    case 'Request failed with status code 400':
        return true;
    }
    return false;
}

export interface NewLogEntryRequest {
    message: string;
    url?: string;
    line?: number;
    column?: number;
    stack?: string;
    pageRequestID?: MaybePromise<string>;
}

export async function NewLogEntry(entry: NewLogEntryRequest) {
    entry.pageRequestID ??= GetPageRequestID();
    entry.url ??= window.location.href;

    return axios.post('/logs', {
        'message': entry.message,
        'url': entry.url,
        'line': entry.line,
        'column': entry.column,
        'stack': entry.stack,
        'pageRequestID': await entry.pageRequestID,
    });
}

export function InitClientSideErrorLogging() {
    window.addEventListener('error', e => {
        if (isIgnoredError(e.message)) {
            return;
        }

        void NewLogEntry({
            message: e.message,
            url: e.filename,
            line: e.lineno,
            column: e.colno,
            stack: object.IsObject(e.error) ? String(e.error['stack']) : undefined,
        });

        return false;
    });

    window.addEventListener('unhandledrejection', e => {
        if (e.reason instanceof Error) {
            if (isIgnoredError(e.reason.message)) {
                return;
            }

            void NewLogEntry({
                message: e.reason.message,
                url: object.IsObject(e.reason) ? String(e.reason['filename']) : undefined,
                line: object.IsObject(e.reason) ? Number(e.reason['lineno']) : undefined,
                column: object.IsObject(e.reason) ? Number(e.reason['colno']) : undefined,
                stack: object.IsObject(e.reason) ? String(e.reason['stack']) : undefined,
            });
        }
    });
}